/**
 * SeatAssignmentRepository.ts
 * 학생 좌석 배정 API 호출
 */
import { API_BASE_URL } from '../../../config/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export interface Student {
  id: string;
  name: string;
  phone: string;
  profile_image_url?: string;
  current_seat_id?: string;
  current_seat_code?: string;
  ticket_id?: string;
  ticket_name?: string;
  ticket_expires_at?: string;
}

export interface SeatStatus {
  student_id: string;
  seat_id: string;
  seat_code: string;
  floor_name: string;
  assigned_at: string;
}

export interface AssignSeatRequest {
  seat_id: string;
  auto_release_previous?: boolean;
}

export class SeatAssignmentRepository {
  /**
   * 학생 검색
   */
  async searchStudents(query: string): Promise<Student[]> {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/students/search?q=${encodeURIComponent(query)}`,
      {
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error('학생 검색에 실패했습니다');
    }

    return await response.json();
  }

  /**
   * 학생 좌석 상태 조회
   */
  async getStudentSeatStatus(studentId: string): Promise<SeatStatus | null> {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/students/${studentId}/seat-status`,
      {
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error('좌석 상태 조회에 실패했습니다');
    }

    return await response.json();
  }

  /**
   * 좌석 배정
   */
  async assignSeat(
    studentId: string,
    request: AssignSeatRequest
  ): Promise<{ message: string; seat_status: SeatStatus }> {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/students/${studentId}/assign-seat`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '좌석 배정에 실패했습니다');
    }

    return await response.json();
  }

  /**
   * 좌석 해제
   */
  async releaseSeat(studentId: string): Promise<{ message: string }> {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/students/${studentId}/release-seat`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '좌석 해제에 실패했습니다');
    }

    return await response.json();
  }

  /**
   * 배정 히스토리 조회
   */
  async getAssignmentHistory(
    studentId: string,
    limit: number = 10
  ): Promise<
    Array<{
      seat_code: string;
      floor_name: string;
      assigned_at: string;
      released_at?: string;
    }>
  > {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/students/${studentId}/assignment-history?limit=${limit}`,
      {
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error('배정 히스토리 조회에 실패했습니다');
    }

    return await response.json();
  }
}

export const seatAssignmentRepository = new SeatAssignmentRepository();
